import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { track } from '../api/events.js';
import { InvoiceBadge } from '../components/Invoice.js';
import { formatMoney, formatPrice } from '../components/site.js';
import {
  Button,
  Card,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  Segmented,
  TableBox,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';
import { useMe } from '../session.js';
import { isRunning } from '../subscription.js';

type Interval = 'month' | 'year';
type Billing = Schemas['Billing'];
type Plan = Schemas['Plan'];
type Point = 'sites' | 'check' | 'links' | 'recipientsHistory' | 'recipientsChannels' | 'logo';

/** Opens a Stripe page (Checkout or Portal) in this tab. */
const go = (url: string) => {
  window.location.assign(url);
};
const checkoutUrl = (plan: string, interval: Interval) =>
  `/billing/checkout?plan=${plan}&interval=${interval}`;

/** The four points of each plan card (billing.html): the words of the sheet, the plan's numbers. */
const POINTS: Record<Plan['code'], Point[]> = {
  free: ['sites', 'check', 'links', 'recipientsHistory'],
  starter: ['sites', 'check', 'links', 'recipientsChannels'],
  business: ['sites', 'check', 'links', 'recipientsChannels'],
  agency: ['sites', 'check', 'recipientsChannels', 'logo'],
};

function usePoint() {
  const { t } = useTranslation();
  return (kind: Point, p: Plan) => {
    const l = p.limits;
    const recipients = t('billing.points.recipients', { count: l.alertRecipients });
    const channels = l.webhookAlerts
      ? t('billing.points.webhook')
      : l.chatAlerts
        ? t('billing.points.chat')
        : null;
    switch (kind) {
      case 'sites':
        return t('billing.points.sites', { count: l.maxSites });
      // "From 2 locations" only while the second location runs (audit, 10.3): nothing says so here.
      case 'check':
        // "Every minute" by itself: a Ukrainian _one would also take 21 or 31 minutes.
        return l.minIntervalSeconds === 60
          ? t('billing.points.checkMinute')
          : t('billing.points.check', { count: l.minIntervalSeconds / 60 });
      case 'links':
        return t('billing.points.links', {
          pages: l.crawlMaxPages,
          every:
            l.crawlEveryDays === 7
              ? t('billing.points.weekly')
              : l.crawlEveryDays === 1
                ? t('billing.points.daily')
                : t('billing.points.every', { count: l.crawlEveryDays }),
        });
      case 'recipientsHistory':
        return t('billing.points.history', {
          text: recipients,
          history: t('billing.points.historyDays', { count: l.historyDays }),
        });
      case 'recipientsChannels':
        // The logo goes with the channels on a plan that shows four points without one of its own.
        return [
          recipients,
          channels,
          l.reportBranding && !POINTS[p.code].includes('logo') ? t('billing.points.logo') : null,
        ]
          .filter(Boolean)
          .join(', ');
      case 'logo':
        return [
          l.reportBranding ? t('billing.points.logo') : null,
          l.serviceDiscountPct ? t('billing.points.discount', { pct: l.serviceDiscountPct }) : null,
        ]
          .filter(Boolean)
          .join(', ');
    }
  };
}

/** "Ihr Tarif" (sheet: .plan-now): the plan, until when, the next step, and what is used. */
function PlanNow({ billing: b, recommended }: { billing: Billing; recommended: string }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const date = (iso: string) => formatDate(iso, i18n.language, timeZone);
  const portal = useMutation({
    mutationFn: () => unwrap(api.POST('/billing/portal-session')),
    onSuccess: (data) => go(data.url),
  });
  const sub = b.subscription;
  const trial = b.trialEndsAt !== null;
  const trialDays = b.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(b.trialEndsAt).getTime() - Date.now()) / 86_400_000))
    : 0;
  const stripe = b.mode === 'stripe' && sub?.provider === 'stripe';
  const endless = endlessFreeAccess(sub);

  let when: string;
  if (trial) when = t('billing.trial', { count: trialDays, date: date(b.trialEndsAt ?? '') });
  // Access granted by us for free (SP-D15): with its end, or without one.
  else if (sub?.provider === 'manual' && sub.complimentary && sub.status === 'active')
    when = sub.currentPeriodEnd
      ? t('billing.freeUntil', { date: date(sub.currentPeriodEnd) })
      : t('billing.freeEndless');
  else if (sub?.provider === 'manual')
    when =
      sub.status === 'active' && sub.currentPeriodEnd
        ? `${t('billing.paidUntil', { date: date(sub.currentPeriodEnd) })} · ${t('billing.invoiceRenewal')}`
        : t('billing.ended');
  else if (sub)
    when = [
      sub.cancelAtPeriodEnd
        ? t('billing.endsOn', { date: date(sub.currentPeriodEnd ?? '') })
        : sub.currentPeriodEnd
          ? t('billing.renewsOn', { date: date(sub.currentPeriodEnd) })
          : null,
      t('billing.online'),
    ]
      .filter(Boolean)
      .join(' · ');
  else when = b.planCode === 'free' ? t('billing.free') : '';

  let action = null;
  if (stripe)
    action = (
      <Button busy={portal.isPending} onClick={() => portal.mutate()}>
        {t('billing.portal')}
      </Button>
    );
  else if (trial || b.planCode === 'free')
    action = (
      <LinkButton
        to={checkoutUrl(trial ? b.planCode : recommended, 'month')}
        variant="primary"
        onClick={() =>
          track('upgrade_clicked', { plan: trial ? b.planCode : recommended, source: 'billing' })
        }
      >
        {t('billing.choose')}
      </LinkButton>
    );
  else if (sub && !endless)
    action = (
      <LinkButton to={checkoutUrl(b.planCode, sub.interval)} variant="secondary">
        {t('billing.renew')}
      </LinkButton>
    );

  const use = (label: string, used: number, max: number) => (
    <div>
      <span>{label}</span>
      <b>
        {used} / {max}
      </b>
      <div className="bar" aria-hidden="true">
        {/* A width from data: a style property through the CSSOM, which the CSP allows. */}
        <i style={{ width: `${Math.round((Math.min(used, max) / Math.max(max, 1)) * 100)}%` }} />
      </div>
    </div>
  );
  return (
    <Card>
      <h2>{t('billing.yourPlan')}</h2>
      <div className="plan-now">
        <div>
          <div className="name">
            {t(`admin.plans.${b.planCode}`)}
            {trial ? (
              <small>{t('billing.trialTag')}</small>
            ) : sub ? (
              <small>{t(`billing.intervalSmall.${sub.interval}`)}</small>
            ) : null}
          </div>
          {when ? <p className="when">{when}</p> : null}
        </div>
        {action}
        <div className="use">
          {use(t('billing.usageSites'), b.usage.activeSites, b.usage.maxSites)}
          {use(t('billing.usageRecipients'), b.usage.recipients, b.usage.maxRecipients)}
        </div>
      </div>
      {portal.isError ? <Notice kind="error">{errorMessage(portal.error, t)}</Notice> : null}
    </Card>
  );
}

/**
 * Free access without an end (SP-D15): nothing to extend. A paid order of the plan would start a
 * term from its payment and so give the access an end.
 */
function endlessFreeAccess(sub: Billing['subscription']): boolean {
  return (
    sub?.provider === 'manual' &&
    sub.complimentary &&
    sub.status === 'active' &&
    sub.currentPeriodEnd === null
  );
}

function PlanTable({ billing, plans }: { billing: Billing; plans: Plan[] }) {
  const { t, i18n } = useTranslation();
  const point = usePoint();
  const [interval, setInterval] = useState<Interval>('month');
  // A Stripe subscription is changed in the customer portal; otherwise every plan is ordered on
  // the checkout page (address, VIES, invoice or online payment: T-63).
  const stripeSubscribed = billing.subscription?.provider === 'stripe';
  // The trial's plan is not the current plan (audit, 10.6): it is chosen like any other.
  const currentCode = billing.trialEndsAt ? null : billing.planCode;
  const paidCurrent = currentCode !== null && currentCode !== 'free';
  const endless = endlessFreeAccess(billing.subscription);
  const yes = (on: boolean) => (on ? '✓' : '–');
  const rows: [string, (p: Plan) => string][] = [
    ['sites', (p) => String(p.limits.maxSites)],
    ['interval', (p) => t('billing.minutes', { count: p.limits.minIntervalSeconds / 60 })],
    ['pages', (p) => String(p.limits.crawlMaxPages)],
    ['recipients', (p) => String(p.limits.alertRecipients)],
    ['chat', (p) => yes(p.limits.chatAlerts)],
    ['webhook', (p) => yes(p.limits.webhookAlerts)],
    [
      'keywords',
      (p) => (p.limits.keywordMonitorsPerSite ? String(p.limits.keywordMonitorsPerSite) : '–'),
    ],
    ['aiFixes', (p) => (p.limits.fixPackagesPerMonth ? String(p.limits.fixPackagesPerMonth) : '–')],
    ['digest', (p) => yes(p.limits.weeklyDigest)],
    ['report', (p) => yes(p.limits.siteReport)],
    ['logo', (p) => yes(p.limits.reportBranding)],
    [
      'history',
      (p) =>
        p.limits.historyDays === 365
          ? t('billing.oneYear')
          : t('billing.days', { count: p.limits.historyDays }),
    ],
    ['discount', (p) => (p.limits.serviceDiscountPct ? `−${p.limits.serviceDiscountPct} %` : '–')],
  ];
  const name = (code: string) => t(`admin.plans.${code}`);

  return (
    <Card>
      <h2>{t('billing.compare')}</h2>
      <div className="interval">
        <span>{t('billing.intervalLabel')}</span>
        <Segmented
          label={t('billing.intervalLabel')}
          value={interval}
          onChange={setInterval}
          options={[
            ['month', t('billing.intervals.month')],
            ['year', t('billing.intervals.year')],
          ]}
        />
        <span className="bonus">{t('billing.yearBonus')}</span>
      </div>
      <div className="plans">
        {plans.map((p) => {
          const current = p.code === currentCode;
          // Per year as on the landing page (audit, decision 3).
          const price = interval === 'month' ? p.priceMonthCents : p.priceYearCents;
          const classes = ['plan', p.badge === 'recommended' ? 'rec' : '', current ? 'current' : '']
            .filter(Boolean)
            .join(' ');
          let action = null;
          if (p.code === 'free' || (current && endless))
            action = current ? (
              // Not to be pressed, yet drawn in full as in the sheet (aria-disabled, not faded).
              <Button variant="secondary" aria-disabled="true">
                {t('billing.current')}
              </Button>
            ) : (
              <span className="hint">{t('billing.freeNote')}</span>
            );
          else if (!stripeSubscribed)
            action = (
              <LinkButton
                to={checkoutUrl(p.code, interval)}
                variant={
                  current || (p.badge === 'recommended' && !paidCurrent) ? 'primary' : 'secondary'
                }
                onClick={() => track('upgrade_clicked', { plan: p.code, source: 'billing' })}
              >
                {current ? t('billing.renew') : t('billing.choose')}
              </LinkButton>
            );
          return (
            <div key={p.code} className={classes}>
              <h3 className="name">
                {name(p.code)}
                {current ? (
                  <span className="cur">{t('billing.current')}</span>
                ) : p.badge ? (
                  <span className={`badge ${p.badge === 'recommended' ? 'acc' : 'neutral'} nodot`}>
                    {t(`billing.badge.${p.badge}`)}
                  </span>
                ) : null}
              </h3>
              <div className="price">
                {formatPrice(price, i18n.language)}
                <small>/ {t(`billing.per.${interval}`)}</small>
              </div>
              <ul>
                {POINTS[p.code].map((kind) => (
                  <li key={kind}>{point(kind, p)}</li>
                ))}
              </ul>
              {action}
            </div>
          );
        })}
      </div>
      <p className="hint">
        {stripeSubscribed ? t('billing.changeInPortal') : t('billing.invoiceHint')}
        {!stripeSubscribed && billing.methods.includes('online')
          ? ` ${t('billing.onlineHint')}`
          : ''}
      </p>
      <TableBox label={t('billing.compare')}>
        <table className="cmp">
          <thead>
            <tr>
              <td />
              {plans.map((p) => (
                <th
                  key={p.code}
                  scope="col"
                  className={p.code === currentCode ? 'current' : undefined}
                >
                  {name(p.code)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, value]) => (
              <tr key={key}>
                <th scope="row">{t(`billing.rows.${key}`)}</th>
                {plans.map((p) => {
                  const v = value(p);
                  return (
                    <td key={p.code} className={v === '–' ? 'dash' : undefined}>
                      {v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableBox>
    </Card>
  );
}

/** Fewer sites in the plan than the account has: which stay active (sheet: .sites-pick). */
function ActiveSites({ billing }: { billing: Billing }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [chosen, setChosen] = useState<string[]>(
    billing.sites.filter((s) => s.status === 'active').map((s) => s.id),
  );
  const save = useMutation({
    mutationFn: () => unwrap(api.POST('/billing/active-sites', { body: { siteIds: chosen } })),
    onSuccess: (data) => {
      client.setQueryData(['billing'], data);
      void client.invalidateQueries({ queryKey: ['sites'] });
    },
  });
  const max = billing.usage.maxSites;
  return (
    <Card>
      <h2>
        {t('billing.activeTitle')} <small>{t('billing.activeText', { count: max })}</small>
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="sites-pick">
          {billing.sites.map((s) => (
            <label key={s.id}>
              <input
                type="checkbox"
                checked={chosen.includes(s.id)}
                disabled={!chosen.includes(s.id) && chosen.length >= max}
                onChange={(e) =>
                  setChosen(
                    e.target.checked ? [...chosen, s.id] : chosen.filter((id) => id !== s.id),
                  )
                }
              />
              <span>{s.name}</span>
              {s.status === 'active' ? (
                <span className="badge ok nodot">{t('status.up')}</span>
              ) : (
                <span className="badge neutral nodot">
                  {s.pausedByPlan ? t('billing.pausedByPlan') : t('status.paused')}
                </span>
              )}
            </label>
          ))}
        </div>
        {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
        {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
        <div className="acts">
          <Button type="submit" busy={save.isPending} disabled={chosen.length === 0}>
            {t('billing.activeSave')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Invoices() {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => unwrap(api.GET('/billing/invoices')),
  });
  if (invoices.isPending) return null;
  if (invoices.isError)
    return (
      <LoadError
        message={errorMessage(invoices.error, t)}
        onRetry={() => void invoices.refetch()}
      />
    );
  return (
    <Card>
      <h2>{t('billing.invoices')}</h2>
      {invoices.data.items.length === 0 ? (
        <p className="hint">{t('billing.noInvoices')}</p>
      ) : (
        <ul className="inv">
          {invoices.data.items.map((inv) => {
            return (
              <li key={inv.id}>
                <span className="no">{inv.number}</span>
                <div className="what">
                  <b>{inv.description}</b>
                  <span>{formatDate(inv.issuedAt, i18n.language, timeZone)}</span>
                </div>
                <span className="sum">{formatMoney(inv.grossCents, i18n.language)}</span>
                <InvoiceBadge invoice={inv} />
                <LinkButton to={`/billing/invoices/${inv.id}`} variant="secondary" size="sm">
                  {inv.status === 'open' ? t('billing.payInvoice') : t('billing.showInvoice')}
                </LinkButton>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/** How often the page asks after Checkout: every 3 s, two minutes. */
const POLLS = 40;
const tries = (state: { dataUpdateCount: number; errorUpdateCount: number } | undefined) =>
  (state?.dataUpdateCount ?? 0) + (state?.errorUpdateCount ?? 0);

/** S11 "Tarif & Rechnungen" (US-08, billing.html). */
export function Billing() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const timeZone = useMe().data?.account.timezone;
  const client = useQueryClient();
  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => unwrap(api.GET('/billing')),
    // After Checkout the webhook activates the plan within seconds (AC ≤ 30 s): asked again until
    // the subscription is paid for, 40 answers (two minutes) at most, failed ones too (SP-A2.76).
    refetchInterval: (query) =>
      params.get('checkout') === 'success' &&
      !isRunning(query.state.data?.subscription) &&
      tries(query.state) < POLLS
        ? 3000
        : false,
  });
  const late = tries(client.getQueryState(['billing'])) >= POLLS;
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => unwrap(api.GET('/billing/plans')) });

  let content;
  if (billing.isPending || plans.isPending) content = <Loading />;
  else if (billing.isError)
    content = (
      <LoadError message={errorMessage(billing.error, t)} onRetry={() => void billing.refetch()} />
    );
  else if (plans.isError)
    content = (
      <LoadError message={errorMessage(plans.error, t)} onRetry={() => void plans.refetch()} />
    );
  else {
    const b = billing.data;
    const sub = b.subscription;
    // The sites a trial has beyond its one site keep running until it ends (SP-C1.1): no choice.
    const tooMany = !b.trialEndsAt && b.usage.sites > b.usage.maxSites;
    const waiting = b.sites.filter((s) => s.pausedByPlan).length;
    const recommended = plans.data.items.find((p) => p.badge === 'recommended')?.code ?? 'business';
    const next = sub?.currentPeriodEnd
      ? t(sub.provider === 'manual' ? 'billing.paidUntil' : 'billing.renewsOn', {
          date: formatDate(sub.currentPeriodEnd, i18n.language, timeZone),
        })
      : null;
    content = (
      <div className="acct wide">
        {params.get('checkout') === 'success' ? (
          isRunning(sub) && sub ? (
            <Notice kind="success" title={t('billing.thanks')}>
              {next
                ? t('billing.thanksText', {
                    plan: t(`admin.plans.${b.planCode}`),
                    interval: t(`billing.intervalSmall.${sub.interval}`),
                    when: next,
                  })
                : null}
            </Notice>
          ) : late ? (
            // The webhook is late: Stripe sends it again within minutes (review of SP-A2.76).
            <Notice
              kind="warning"
              action={
                <Button variant="secondary" size="sm" onClick={() => void billing.refetch()}>
                  {t('app.retry')}
                </Button>
              }
            >
              {t('billing.activatingLate')}
            </Notice>
          ) : (
            <Notice kind="info">{t('billing.activating')}</Notice>
          )
        ) : null}
        {params.get('checkout') === 'cancelled' ? (
          <Notice kind="info">{t('billing.cancelled')}</Notice>
        ) : null}
        {sub?.pastDueSince ? (
          <Notice kind="error" title={t('billing.pastDueTitle')}>
            {t('billing.pastDue')}
          </Notice>
        ) : null}
        {waiting > 0 ? (
          <Notice kind="info">{t('billing.pausedNotice', { count: waiting })}</Notice>
        ) : null}
        {b.mode === 'invoice' && !b.live ? (
          <Notice kind="warning" title={t('billing.demoTitle')}>
            {t('billing.demo')}
          </Notice>
        ) : null}
        <PlanNow billing={b} recommended={recommended} />
        {tooMany ? <ActiveSites key={b.planCode} billing={b} /> : null}
        <PlanTable billing={b} plans={plans.data.items} />
        <Invoices />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-title">
        <h1>{t('billing.title')}</h1>
      </div>
      {content}
    </div>
  );
}
